import { AxiosResponse, AxiosPromise } from 'axios';
import { AnyAction, Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { AsyncAction } from 'redux-promise-middleware';
import {
  ServerResponse,
  Operation,
} from 'data/types';
import { RootState } from 'data/root.reducers';
import { FieldUpdateHandlerParam } from 'shared/form';
import { VpnConfig } from '../vpnConfig/vpnConfig.types';

export const API_PATH_IP_ASSIGNMENTS = '/ipassignment';
export const API_PATH_AF_GROUPS = '/af-groups';
export const AF_USER_CATEGORY_SETTINGS = 'IpAssignmentSettings';
export const GO_TO_IP_ASSIGNMENT_CREATE = 'GO_TO_IP_ASSIGNMENT_CREATE';
export const GO_TO_IP_ASSIGNMENT_EDIT = 'GO_TO_IP_ASSIGNMENT_EDIT';
export const GO_TO_IP_ASSIGNMENT_VIEW = 'GO_TO_IP_ASSIGNMENT_VIEW';
export const GO_TO_IP_ASSIGNMENT_COPY = 'GO_TO_IP_ASSIGNMENT_COPY';
export const UPDATE_IP_ASSIGNMENT = 'UPDATE_IP_ASSIGNMENT';
export const UPDATE_IP_ASSIGNMENT_FULFILLED = 'UPDATE_IP_ASSIGNMENT_FULFILLED';
export const UPDATE_IP_ASSIGNMENT_REJECTED = 'UPDATE_IP_ASSIGNMENT_REJECTED';
export const SELECT_IP_ASSIGNMENT = 'SELECT_IP_ASSIGNMENT';
export const AUDIT_IP_ASSIGNMENT = 'AUDIT_IP_ASSIGNMENT';
export const RESET_IP_ASSIGNMENT = 'RESET_IP_ASSIGNMENT';
export const IP_ASSIGNMENT_INTERFACE_TEMPORARY_ID = 'ipAssignmentInterface';
export const IP_ASSIGNMENT_REQ_STATE_FIELD = 'reqState';
export const IP_ASSIGNMENT_STATE_FIELD = 'state';

export const ANY_ADDRESSES = ['ANY_ADDR', 'ADDR_ANY'];

export type ConfiguredIpAssignment = {
  afTag: string
  xcipioAfType: string
  model: string
}

export type IpAssignmentMap = {
  [ipAssignmentKey: string]: IpAssignment[]
}

export type AfcfByServiceMap = {
  [service: string]: {
    collectionFunctions: {
      [collectionFunctionKey: string]: {
        ipAssignments: string[]
      }
    }
    ipAssignments: string[]
  }
}

export enum IpAssignmentPrivileges {
  View = 'af_view',
  Edit = 'af_write',
  Delete = 'af_delete',
}

export interface AdditionalMO {
  id?: string
  operation?: Operation
  moName: string,
  options: { key: string, value: string | boolean }[]
}

export interface IpAssignmentInterface {
  id: string
  name?: string
  moName?: string
  deliveryVia?: string
  options: {
    key: string
    value: string | boolean
  }[]
  operation?: Operation
  additionalMOs?: AdditionalMO[]
}

export interface IpAssignmentDataActionDetails { action: 'AUDIT' | 'DWNLD' | 'REBOOT' | 'SHUTDOWN' | 'RESET' | 'RELOAD' | 'NONE' }

export type ContentDeliveryVia = 'directFromAF' | 'trafficManager' | 'ss8Distributor';
export const POSSIBLE_CONTENT_DELIVERY_VIA = ['directFromAF', 'trafficManager', 'ss8Distributor'];
export type DataDeliveryVia = 'directFromAF' | 'ss8Distributor';
export const POSSIBLE_DATA_DELIVERY_VIA = ['directFromAF', 'ss8Distributor'];
export interface IpAssignment {
  servId?: string
  servType?: string
  forceOp?: string
  ipAddress?: string
  appId?: string
}

export interface IpAssignmentInterfaceFormData extends Omit<IpAssignmentInterface, 'options'> {
  destIPAddr?: string
  destPort?: string
  dscp?: string
  ownnetid?: string
  reqState?: 'ACTIVE' | 'INACTIVE'
  reqstate?: 'ACTIVE' | 'INACTIVE'
  secinfoid?: string
  state?: string
  tpkt?: boolean
  x224headerpresent?: boolean
  ownIPPort?: string
  mscid?: string
  imei?: boolean
  imsi?: boolean
  msisdn?: boolean
  timeout?: string
  netid?: string
  transport?: string
  maxx2connsperaf?: string,
  maxx2connections?: string,
  appProtocol?: string
  appprotocol?: string
  appprotocolver?: string
  version?: string
  iri_payload_encoding?: string // eslint-disable-line camelcase
  keepaliveinterval?: string
  litargetnamespace?: string
  uri?: string
  lbid?: string
  traceLevel?: string
  trclvl?: string
  type?: string
  litype?: string
  ccnetid?: string
  cdnetid?: string
  cdport?: string
  interval?: string
  ipduid?: string
  command?: string
  dfid?: string,
  deliveryVia?: string
  role?: 'CLIENT' | 'SERVER'
  sharedsecret?: string
  ownIPAddr?: string
  ownipaddr?: string
  password?: string
  rmtpassword?: string
  userPassword?: string
  persistent?: boolean
  ftpType?: string
  userName?: string
  username?: string
  serverName?: string
  destPath?: string
  ifid?: string
  mcid?: string
  afid?: string
  mc?: string
  moName?: string
  ccdest?: string
  terrid?: string
  terr?: string
  cctype?: string
  cdtype?: string
  httpversion?: string
  processOptionSecInfo?: string
  serviceid?: string
  sshInfoId?: string
  ccnodename?: string
  mdf2NodeName?: string
  mdf3NodeName?: string
  dataDeliveryVia?: string
  contentDeliveryVia?: string
  connection?: string
  ccdestip?: string
  keepalive?: boolean
  naming?: string,
  agent?: string,
  intgrpid?: string,
  isnegroup?: string,
  parentid?: string,
  resversion?: string,
  netype?: string,
  encryption?: 'ENABLED' | 'DISABLED',
  neid?: string,
  responsetimeout?: string,
  rolepriority?: 'PRIMARY' | 'SECONDARY',
  authentication?: string,
  retryInterval?: string,
  keepAliveInterval?: string,
  KEEPALIVEINTERVAL?: string,
  securitylvl?: string,
  sharedkey?: string,
  numtarget?: string,
  mac?: string,
  model?: string,
  typeofservice?: string,
  typeofservicemask?: string,
  vpnrouteforward?: boolean,
  domainname?: string,
  retry?: string,
  interfaceid?: string,
  mibver?: string,
  // eslint-disable-next-line camelcase
  tls_alpn?: boolean,
  remoteIp?: string,
  remoteport?: string,
  deliveryFormat?: string,
  ipAddr?: string,
  ipaddr?: string,
  port?: string,
  orighost?: string,
  interface?: string
  dsid?: string,
  niid?: string,
  origrealm?: string,
  remotePort?: string,
  irimcid?: string,
  lea?: string
  nodeNameType?: string,
  // eslint-disable-next-line camelcase
  audit_mechanism?: string,
  afiribuffer?: boolean,
  hbtimeout?: string,
  exportinterval?: string,
  force?: boolean,
  prefix?: string,
}

export type ContentInterfaceFormData = IpAssignmentInterfaceFormData
export type ProvisioningInterfaceFormData = IpAssignmentInterfaceFormData
export type DataInterfaceFormData = IpAssignmentInterfaceFormData

export type IpAssignmentFormSection = 'general' | 'systemInformation' | IpAssignmentInterfaceFormSectionType;
export type IpAssignmentInterfaceFormSectionType = 'provisioningInterfaces'
  | 'dataInterfaces'
  | 'contentInterfaces'
  | 'monitoringCenters'
  | 'territories'
  | 'eimsNetworkElement'
  | 'diameter'
  | 'manualEntry'
  | 'ipAssignment'
  | 'fileImportExport'
  | 'usnbdAgencies'
  | 'usnbdCallDataChannels';

export interface IpAssignmentFormData {
  appId?: string,
  ipAddress: string,
  servId: string,
  servType?: string,
  forceOp: string
}

export interface IpAssignmentDataToSubmit {
  appId: string,
  ipAddress: string,
  servId: string,
  servType: string,
  forceOp: string
}

export interface IpAddressFormData {
  ipAddr: string,
  prefix: string
}

export interface ManualEntryFormData {
  servId: string,
  servType?: string,
  forceOp: string
}

export interface IpAssignmentGroup {
  readonly id: string,
  dxOwner: string
  dxAccess: string
  name: string
  afIds: {
    id: string
    name: string
  }[]
}

export interface IpAssignmentState {
  afs: IpAssignment[]
  afGroups: IpAssignmentGroup[]
  single: IpAssignment | null
  selected: IpAssignment[],
  isDeleting: boolean,
  configuredAfs: ConfiguredIpAssignment[],
}

export interface GoToIpAssignmentCreateAction extends AnyAction {
  type: typeof GO_TO_IP_ASSIGNMENT_CREATE
}

export interface GoToIpAssignmentEditAction extends AnyAction {
  type: typeof GO_TO_IP_ASSIGNMENT_EDIT
}

export interface GoToIpAssignmentViewAction extends AnyAction {
  type: typeof GO_TO_IP_ASSIGNMENT_VIEW
}

export interface GoToIpAssignmentCopyAction extends AnyAction {
  type: typeof GO_TO_IP_ASSIGNMENT_COPY
}

export interface UpdateIpAssignmentThunkAction extends ThunkAction<void, RootState, unknown, Action<string>> { }

export interface UpdateIpAssignmentAction extends AnyAction {
  type: typeof UPDATE_IP_ASSIGNMENT
  payload: AxiosPromise<ServerResponse<void>>
}

export interface UpdateIpAssignmentFulfilledAction extends AnyAction {
  type: typeof UPDATE_IP_ASSIGNMENT_FULFILLED
  payload: AxiosResponse<ServerResponse<void>>
}

export interface SelectIpAssignmentAction extends AnyAction {
  type: typeof SELECT_IP_ASSIGNMENT
  payload: IpAssignment[]
}

export interface AuditIpAssignmentThunkAction extends ThunkAction<void, RootState, void, AnyAction> { }

export interface ResetIpAssignmentThunkAction extends ThunkAction<void, RootState, void, AnyAction> { }

export interface RefreshIpAssignmentThunkAction extends ThunkAction<void, RootState, void, AnyAction> { }

export type IpAssignmentActionTypes =
  GoToIpAssignmentCreateAction |
  GoToIpAssignmentEditAction |
  GoToIpAssignmentViewAction |
  GoToIpAssignmentCopyAction |
  UpdateIpAssignmentAction |
  UpdateIpAssignmentFulfilledAction |
  SelectIpAssignmentAction;
